import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { exportPortfolioToPDF, exportPortfolioToExcel } from '../../utils/export';
import { useAssets, useDeleteAsset } from '../../hooks/useAssets';
import { PageLoader } from '../../components/common/LoadingSpinner';
import { CreateAssetModal } from './CreateAssetModal';
import {
  hideCurrency,
  hidePercent,
  gainLossClass,
} from '../../utils/format';
import { useValuesVisibility } from '../../stores/valuesVisibilityStore';
import {
  ASSET_TYPE_LABELS,
  ASSET_TYPE_BG,
  type AssetType,
} from '../../types';

export default function AssetsPage() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<AssetType | ''>('');
  const { data: assets, isLoading } = useAssets(typeFilter ? { type: typeFilter } : undefined);
  const { mutate: deleteAsset } = useDeleteAsset();
  const { isVisible } = useValuesVisibility();

  const filtered = assets?.filter((a) =>
    search === '' ||
    a.code.toLowerCase().includes(search.toLowerCase()) ||
    a.name.toLowerCase().includes(search.toLowerCase()),
  );

  const assetTypes = Object.keys(ASSET_TYPE_LABELS) as AssetType[];

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div>
          <h1 className="page-title">Ativos</h1>
          <p className="page-subtitle">{assets?.length ?? 0} ativos cadastrados</p>
        </div>
        <div className="flex items-center gap-2">
          {assets && assets.length > 0 && (
            <>
              <button
                onClick={() => exportPortfolioToExcel(assets)}
                className="btn-ghost flex items-center gap-1.5 text-xs"
                title="Exportar Excel"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Excel
              </button>
              <button
                onClick={() => exportPortfolioToPDF(assets)}
                className="btn-ghost flex items-center gap-1.5 text-xs"
                title="Exportar PDF"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                PDF
              </button>
            </>
          )}
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Novo Ativo
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
          <input
            type="text"
            placeholder="Buscar por código ou nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-9"
          />
        </div>
        <div className="relative">
          <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as AssetType | '')}
            className="input pl-9 pr-8 appearance-none"
          >
            <option value="">Todos os tipos</option>
            {assetTypes.map((type) => (
              <option key={type} value={type}>
                {ASSET_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {filtered && filtered.length > 0 ? (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Ativo</th>
                <th>Tipo</th>
                <th className="text-right">Qtd.</th>
                <th className="text-right">Preço Médio</th>
                <th className="text-right">Preço Atual</th>
                <th className="text-right">Total Invest.</th>
                <th className="text-right">Valor Atual</th>
                <th className="text-right">Lucro/Prej.</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((asset) => {
                const totalInvested = asset.quantity * asset.averagePrice;
                const currentValue = asset.quantity * (asset.currentPrice ?? asset.averagePrice);
                const gainLoss = currentValue - totalInvested;
                const gainLossPercent =
                  totalInvested > 0 ? (gainLoss / totalInvested) * 100 : 0;

                return (
                  <tr
                    key={asset.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/assets/${asset.id}`)}
                  >
                    <td>
                      <div>
                        <span className="font-medium text-white font-mono">{asset.code}</span>
                        <p className="text-xs text-white/40 truncate max-w-[160px]">
                          {asset.name}
                        </p>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge text-xs ${ASSET_TYPE_BG[asset.type as AssetType] || 'badge-blue'}`}
                      >
                        {ASSET_TYPE_LABELS[asset.type as AssetType] || asset.type}
                      </span>
                    </td>
                    <td className="text-right font-mono text-sm">{asset.quantity}</td>
                    <td className="text-right font-mono text-sm">
                      {hideCurrency(asset.averagePrice, isVisible)}
                    </td>
                    <td className="text-right font-mono text-sm">
                      {asset.currentPrice ? hideCurrency(asset.currentPrice, isVisible) : '—'}
                    </td>
                    <td className="text-right font-mono text-sm">
                      {hideCurrency(totalInvested, isVisible)}
                    </td>
                    <td className="text-right font-mono text-sm">
                      {hideCurrency(currentValue, isVisible)}
                    </td>
                    <td className="text-right">
                      <div>
                        <p className={`text-sm font-medium font-mono ${gainLossClass(gainLoss)}`}>
                          {hideCurrency(gainLoss, isVisible)}
                        </p>
                        <p className={`text-xs ${gainLossClass(gainLossPercent)}`}>
                          {gainLossPercent >= 0 ? (
                            <ArrowUpIcon className="inline h-3 w-3" />
                          ) : (
                            <ArrowDownIcon className="inline h-3 w-3" />
                          )}
                          {hidePercent(gainLossPercent, isVisible)}
                        </p>
                      </div>
                    </td>
                    <td
                      onClick={(e) => e.stopPropagation()}
                      className="text-right"
                    >
                      <button
                        onClick={() => {
                          if (confirm(`Remover ${asset.code}?`)) {
                            deleteAsset(asset.id);
                          }
                        }}
                        className="btn-ghost btn-sm text-danger-500 hover:text-danger-400"
                      >
                        Remover
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card flex flex-col items-center justify-center py-16 text-center">
          <div className="h-16 w-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
            <PlusIcon className="h-8 w-8 text-white/20" />
          </div>
          <h3 className="text-white font-medium">Nenhum ativo encontrado</h3>
          <p className="text-white/40 text-sm mt-1">
            {search ? 'Tente outro termo de busca' : 'Comece cadastrando seu primeiro ativo'}
          </p>
          {!search && (
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary mt-4"
            >
              <PlusIcon className="h-4 w-4" />
              Cadastrar Ativo
            </button>
          )}
        </div>
      )}

      {showModal && (
        <CreateAssetModal onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}
